/**
 * Application Modal
 * Multi-step loan application flow with all form sections
 */

import type {
  DtoBorrowerProgress,
  DtoCoBorrowerProgress,
  DtoLenderConfiguration,
} from "@porchpass/api-client";
import {
  deleteDealerPortalV1DealerIdBorrowerBorId,
  DomainApplicationStatus,
  DomainBorrowerStatus,
} from "@porchpass/api-client";
import {
  AlertCircleIcon,
  ChevronRightIcon,
  DropdownMenuItem,
  FileAddIcon,
  FullscreenModal,
  FullscreenModalContent,
  FullscreenModalTitle,
  NavigationToolbar,
  PencilEdit02Icon,
  Spinner,
  TableOfContents,
  toast,
  Transition,
  useApiCall,
  type NavigationStep,
  type TableOfContentsItem,
} from "@porchpass/ui";
import * as Sentry from "@sentry/react";
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { auth } from "src/api/firebase";
import { useToast } from "src/components/Toast";
import { useDgaModal } from "src/context/DgaModalContext";
import { useDealer } from "src/hooks/useDealer";
import { AddCoBorrowerDropdown } from "./components/AddCoBorrowerDropdown";
import { ApplicationLockConflictDialog } from "./components/ApplicationLockConflictDialog";
import { CoBorrowerEmptyStateGraphic } from "./components/CoBorrowerEmptyStateGraphic";
import { DgaSaveStatus } from "./components/DgaSaveStatus";
import DraftCreationStep from "./components/DraftCreationStep";
import InviteCoBorrowerDialog from "./components/InviteCoBorrowerDialog";
import {
  ApplicationFormProvider,
  STEP_ORDER,
  useApplicationFormStore,
} from "./hooks/useApplicationFormStore";
import {
  useDebouncedAutoSave,
  type SaveHandlerConfig,
} from "./autosave/useDebouncedAutoSave";
import { createAutoSaveHandlers } from "./autosave/createAutoSaveHandlers";
import {
  buildSaveErrorMessage,
  classifySaveError,
} from "./autosave/saveErrors";
import {
  computeValidationFlags,
  mapBorrowerProgressToFieldErrors,
  mergeValidationErrors,
  preserveApiFieldErrors,
  summarizeErrorSections,
  validateApplication,
} from "./validation/validateApplication";
import { getFieldDisplayName } from "./constants/fieldDisplayNames";
import { STEP_CONFIG } from "./constants/stepConfig";
import {
  isMailingAddressDuplicateOfCurrent,
  isNonEmploymentIncome,
} from "./utils/borrowerDataUtils";
import { useDgaApi } from "./hooks/useDgaApi";
import { useLockConflictDetection } from "./hooks/useLockConflictDetection";
import { useScrollSpy } from "./hooks/useScrollSpy";
import type {
  ApplicationFormData,
  ApplicationStep,
  BorrowerData,
  LenderConfig,
  SignatureData,
  SignatureStatus,
} from "./types/applicationTypes";
import { createEmptyBorrower } from "./types/applicationTypes";
import {
  mapApiAddressToFrontend,
  mapApiAssetToFrontend,
  mapApiBorrowerToFrontend,
  mapApiDemographicsToFrontend,
  mapApiEmploymentToFrontend,
  mapApiExpenseToFrontend,
  mapApiHomeAddressToFrontend,
  mapApiHomeToFrontend,
  mapApiIncomeToFrontend,
  mapApiLandToFrontend,
} from "./utils/apiModelAdapters";

// Form section components
import { API_PREFIX_PATH } from "src/constants";
import { AdditionalIncomeForm } from "./components/AdditionalIncomeForm";
import { AddressFormSection } from "./components/AddressFormSection";
import { EcoaForm } from "./components/EcoaForm";
import { EmploymentHistoryForm } from "./components/EmploymentHistoryForm";
import { ExpensesForm } from "./components/ExpensesForm";
import { FinancialAccountsForm } from "./components/FinancialAccountsForm";
import { HomeDetailsForm } from "./components/HomeDetailsForm";
import { LandDetailsForm } from "./components/LandDetailsForm";
import { LenderSelectionSection } from "./components/LenderSelectionSection";
import { MaritalStatusForm } from "./components/MaritalStatusForm";
import { NearestRelativeForm } from "./components/NearestRelativeForm";
import { PIIForm } from "./components/PIIForm";
import { PersonalInformationForm } from "./components/PersonalInformationForm";
import { PricingCalculatorSection } from "./components/PricingCalculatorSection";
import PropertyAddressForm from "./components/PropertyAddressForm";
import { RemoveCoBorrowerSection } from "./components/RemoveCoBorrowerSection";
import { SignatureSection } from "./components/SignatureSection";
import { SummarySection } from "./components/SummarySection";

interface ApplicationModalContentProps {
  isOpen: boolean;
  applicationId?: string;
  lockedAppIdRef: React.MutableRefObject<string | null>;
  isCloseLoading: boolean;
}

const ApplicationModalContent: React.FC<ApplicationModalContentProps> = ({
  isOpen,
  applicationId,
  lockedAppIdRef,
  isCloseLoading,
}) => {
  const store = useApplicationFormStore();
  const {
    data,
    currentStep,
    setCurrentStep,
    dirtyFields,
    pausedAutoSaveFields,
    pauseAutoSaveFields,
    clearSavedFields,
    getStepProgress,
    getOverallProgress,
    initializeApplication,
    isLockedForSigning,
    setFrontendValidationPrimaryPassed,
    setFrontendValidationCoPassed,
    setValidationErrors,
    backendValidationErrors,
    setBackendValidationErrors,
    setManualFieldValidationActive,
  } = store;

  const api = useDgaApi();
  const navigate = useNavigate();
  const { dealer_id: dealerId } = useDealer();
  const { closeDgaModal } = useDgaModal();
  const { setPendingToast } = useToast();

  // Derive readyForLenders: all borrowers signed + application submitted
  const readyForLenders = React.useMemo(() => {
    const sig = data.signature;
    const allSigned =
      sig.borrowerStatus === "signed" &&
      (!data.hasCoBorrower || sig.coBorrowerStatus === "signed");
    return (
      allSigned &&
      sig.applicationStatus === DomainApplicationStatus.ApplicationSubmitted
    );
  }, [data.signature, data.hasCoBorrower]);

  const handleSubmitApplication = React.useCallback(async () => {
    const appId = data.applicationId;
    await closeDgaModal();
    if (appId) {
      void navigate(`/${dealerId}/applications/${appId}`);
    }
  }, [closeDgaModal, navigate, dealerId, data.applicationId]);

  // Refs to avoid stale closures in auto-save handlers
  const dataRef = React.useRef(data);
  dataRef.current = data;
  const storeRef = React.useRef(store);
  storeRef.current = store;
  const apiRef = React.useRef(api);
  apiRef.current = api;
  const suppressHandlerToastsRef = React.useRef(false);

  // API-backed auto-save handlers — built from the pure factory in
  // autosave/createAutoSaveHandlers.ts. Accessors read the live refs so the
  // handlers always see the latest data/store/api.
  const autoSaveHandlers = React.useMemo<SaveHandlerConfig>(
    () =>
      createAutoSaveHandlers({
        getData: () => dataRef.current,
        getStore: () => storeRef.current,
        getApi: () => apiRef.current,
        getSuppressHandlerToasts: () => suppressHandlerToastsRef.current,
      }),
    [],
  );

  // Lock conflict detection hook
  const {
    showConflictModal,
    lockedByUser: conflictLockedByUser,
    handleTakeover,
    dismissConflict,
    wrappedOnSaveError,
  } = useLockConflictDetection(data.applicationId, dealerId, (error) => {
    console.error("[ApplicationModal] Auto-save error:", error);
  });

  // Auto-save hook (runs in background)
  const hasUnsavedChanges = React.useMemo(
    () => [...dirtyFields].some((field) => !pausedAutoSaveFields.has(field)),
    [dirtyFields, pausedAutoSaveFields],
  );

  const { isSaving, lastError, lastSavedAt, flushSave } = useDebouncedAutoSave(
    data,
    dirtyFields,
    clearSavedFields,
    autoSaveHandlers,
    {
      debounceMs: 1500,
      maxWaitMs: 5000,
      enabled: !store.isLoading,
      pausedAutoSaveFields,
      onPauseAutoSaveFields: pauseAutoSaveFields,
      suppressHandlerToastsRef,
      onSaveError: (error, failedFields) => {
        void wrappedOnSaveError(error);
        // Lock conflicts surface via the takeover dialog (wrappedOnSaveError);
        // "already in use" and field-scoped failures surface via the save handler.
        // Only generic failures get the retry toast.
        if (classifySaveError(error) === "generic") {
          const message = buildSaveErrorMessage(
            failedFields,
            getFieldDisplayName,
          );
          if (message) toast.error(message);
        }
      },
    },
  );

  const isCreatingCoBorrowerRef = React.useRef(false);
  const [isCreatingCoBorrower, setIsCreatingCoBorrower] = React.useState(false);

  const handleAddCoBorrowerHere = React.useCallback(async () => {
    if (!data.applicationId || data.hasCoBorrower) return;
    if (isCreatingCoBorrowerRef.current) return;
    isCreatingCoBorrowerRef.current = true;
    setIsCreatingCoBorrower(true);
    try {
      const resp = await api.createCoBorrower(data.applicationId);
      const coData = resp.data as { borrower_id?: string };
      if (coData.borrower_id) {
        store.updateCoBorrowerId(coData.borrower_id);
        store.markFieldDirty("coBorrower.dependentsAgesYears");
      }
      store.toggleCoBorrower(true);
    } catch {
      setPendingToast("Failed to add co-borrower. Please try again.", "error");
    } finally {
      isCreatingCoBorrowerRef.current = false;
      setIsCreatingCoBorrower(false);
    }
  }, [data.applicationId, data.hasCoBorrower, api, store, setPendingToast]);

  // Remove the co-borrower from the current Draft application.
  // The button is only visible when hasCoBorrower === true, which (per the
  // create flow guarantee in handleAddCoBorrowerHere) implies coBorrowerId
  // is set. The TypeScript guard below narrows the optional ID and would
  // only fire if the invariant is somehow violated.
  const removeCoBorrowerApi = useApiCall(
    () => {
      if (!dealerId || !data.coBorrowerId) {
        return Promise.reject(
          new Error("Dealer or co-borrower information is missing."),
        );
      }
      return deleteDealerPortalV1DealerIdBorrowerBorId(
        dealerId,
        data.coBorrowerId,
        {},
      );
    },
    {
      toast: {
        success: "Co-borrower removed",
        useServerErrorMessage: true,
      },
      onSuccess: () => {
        // Order matters: clear local state before navigating so the Summary
        // renders once with hasCoBorrower=false instead of flickering.
        store.removeCoBorrower();
        store.setCurrentStep("summary");
      },
    },
  );

  const [inviteDialogOpen, setInviteDialogOpen] = React.useState(false);

  // Fetch dealer lenders when the modal opens (no appId needed).
  // This populates the lenders list immediately, even before the application is created.
  // We store the result in a ref so the init useEffect can overlay app-level selections.
  const dealerLendersRef = React.useRef<LenderConfig[]>([]);
  React.useEffect(() => {
    if (!isOpen) return;
    void (async () => {
      try {
        const resp = await api.fetchLenders();
        const dealerData = resp.data as DtoLenderConfiguration | undefined;
        const lendersList = dealerData?.lenders;
        if (lendersList) {
          const primaryId = dealerData.primary_lender;
          const lenders = lendersList
            .filter((dl: { enabled?: boolean }) => dl.enabled)
            .map((dl: { id?: string; name?: string; logo_url?: string }) => ({
              lenderId: String(dl.id ?? ""),
              lenderName: dl.name ?? "",
              logoUrl: dl.logo_url,
              enabled: true,
              autoSubmit: false,
              isPrimary: dl.id != null && dl.id === primaryId,
              dealerEnabled: true,
            }));
          dealerLendersRef.current = lenders;
          // populateFromApi does NOT mark "lenders" dirty — avoids a spurious POST
          // re-save when loading existing apps (where dirty accumulates while isLoading=true).
          store.populateFromApi({ lenders });
          // For new apps only: queue the initial lender selections for deferred autosave.
          // The deferredFieldsRef mechanism will hold these until the draft is created,
          // then fire saveApplicationLenders with all dealer-enabled lenders enabled.
          if (!applicationId) {
            store.markFieldDirty("lenders");
          }
        }
      } catch {
        // Non-critical — lenders will load later via loadApplication
      }
    })();
  }, [isOpen]);

  // Mirror the store's applicationId into the parent's lockedAppIdRef so the
  // unlock-on-close effect can fire for newly-created drafts (where the id only
  // materializes after DraftCreationStep -> store.updateApplicationIds).
  React.useEffect(() => {
    if (data.applicationId) {
      lockedAppIdRef.current = data.applicationId;
    }
  }, [data.applicationId, lockedAppIdRef]);

  // Initialize application on mount (load existing or start fresh)
  React.useEffect(() => {
    if (!applicationId) return;
    void (async () => {
      await initializeApplication(applicationId);
      try {
        const result = await api.loadApplication(applicationId);

        // Map primary borrower data
        const borrowerData = result.borrower
          ? mapApiBorrowerToFrontend(result.borrower)
          : createEmptyBorrower();
        const rawAddresses = result.addresses?.addresses ?? [];
        const currentAddress = rawAddresses.find(
          (a) => a.address_type === "CURRENT",
        );
        const addresses = rawAddresses
          .filter((a) => !isMailingAddressDuplicateOfCurrent(a, currentAddress))
          .map(mapApiAddressToFrontend);
        const employmentHistory = (
          result.employments?.employment_details ?? []
        ).map(mapApiEmploymentToFrontend);
        const financialAccounts = (result.assets?.assets ?? []).map(
          mapApiAssetToFrontend,
        );
        const additionalIncome = (result.incomes?.incomes ?? [])
          .filter(isNonEmploymentIncome)
          .map(mapApiIncomeToFrontend);
        const expenses = (result.expenses?.expenses ?? []).map(
          mapApiExpenseToFrontend,
        );
        const demographics = result.demographics
          ? mapApiDemographicsToFrontend(result.demographics)
          : {};

        // Map co-borrower data (if present)
        let mappedCoBorrower: BorrowerData | undefined;
        if (result.coBorrowerData) {
          const coBase = mapApiBorrowerToFrontend(
            result.coBorrowerData.borrower,
          );
          const coRawAddresses =
            result.coBorrowerData.addresses?.addresses ?? [];
          const coCurrentAddress = coRawAddresses.find(
            (a) => a.address_type === "CURRENT",
          );
          const coAddresses = coRawAddresses
            .filter(
              (a) => !isMailingAddressDuplicateOfCurrent(a, coCurrentAddress),
            )
            .map(mapApiAddressToFrontend);
          const coEmployment = (
            result.coBorrowerData.employments?.employment_details ?? []
          ).map(mapApiEmploymentToFrontend);
          const coAccounts = (result.coBorrowerData.assets?.assets ?? []).map(
            mapApiAssetToFrontend,
          );
          const coIncome = (result.coBorrowerData.incomes?.incomes ?? [])
            .filter(isNonEmploymentIncome)
            .map(mapApiIncomeToFrontend);
          const coExpenses = (
            result.coBorrowerData.expenses?.expenses ?? []
          ).map(mapApiExpenseToFrontend);
          const coDemographics = result.coBorrowerData.demographics
            ? mapApiDemographicsToFrontend(result.coBorrowerData.demographics)
            : {};
          mappedCoBorrower = {
            ...coBase,
            ...coDemographics,
            hasSufficientAddressHistory:
              result.coBorrowerData.addresses?.has_sufficient_address_history ??
              false,
            hasSufficientEmploymentHistory:
              result.coBorrowerData.employments
                ?.has_sufficient_employment_history ?? false,
            addresses: coAddresses,
            employmentHistory: coEmployment,
            financialAccounts: coAccounts,
            additionalIncome: coIncome,
            expenses: coExpenses,
          };
        }

        // Build home + pricing + land data
        let homeData = {};
        let pricingData = {};
        let landData = {};
        if (result.home) {
          const mapped = mapApiHomeToFrontend(result.home);
          homeData = mapped.home;
          pricingData = mapped.pricing;
          const propertyAddress = mapApiHomeAddressToFrontend(result.home);
          if (propertyAddress) {
            landData = { propertyAddress };
          }
        }
        if (result.land) {
          landData = { ...landData, ...mapApiLandToFrontend(result.land) };
        }
        // Override with values from summary if available (source of truth for pricing)
        if (result.summary) {
          pricingData = {
            ...pricingData,
            homePrice: result.summary.home_price ?? 0,
            downPaymentAmount: result.summary.down_payment_amount ?? 0,
            upgradesCost: result.summary.home_upgrades_cost ?? 0,
            siteImprovementCost: result.summary.site_improvement_amount ?? 0,
            yearlyPremium: result.summary.home_insurance_yearly_payment ?? 0,
            loanAmount: result.summary.loan_total_amount ?? 0,
            landPurchaseOrPayoffAmount:
              result.summary.land_purchase_or_payoff_amount,
          };
        }

        // Overlay saved application_lenders onto the dealer lender list.
        if (result.appLenders && dealerLendersRef.current.length > 0) {
          const appSelections = result.appLenders; // DomainApplicationLenderConfiguration[]
          const merged = dealerLendersRef.current.map((l) => {
            const appEntry = appSelections.find(
              (a) => String(a.id ?? "") === l.lenderId,
            );
            if (appEntry == null) {
              return { ...l, enabled: false, autoSubmit: false };
            }
            return {
              ...l,
              enabled: l.isPrimary ? true : (appEntry.enabled ?? false),
              autoSubmit: appEntry.auto_submit ?? false,
            };
          });
          dealerLendersRef.current = merged;
          // populateFromApi does NOT mark dirty — we just loaded this data, no need to re-save.
          // The subsequent populateFromApi({ applicationId, ... }) does not include 'lenders',
          // so these merged selections are preserved through to setIsLoading(false).
          store.populateFromApi({ lenders: merged });
        }

        // Bulk populate store WITHOUT marking fields dirty
        store.populateFromApi({
          applicationId,
          primaryBorrowerId: result.primaryBorrowerId,
          coBorrowerId: result.coBorrowerIds[0],
          hasCoBorrower: result.coBorrowerIds.length > 0,
          coBorrower:
            result.coBorrowerIds.length > 0
              ? (mappedCoBorrower ?? createEmptyBorrower())
              : undefined,
          primaryBorrower: {
            ...borrowerData,
            ...demographics,
            hasSufficientAddressHistory:
              result.addresses?.has_sufficient_address_history ?? false,
            hasSufficientEmploymentHistory:
              result.employments?.has_sufficient_employment_history ?? false,
            addresses,
            employmentHistory,
            financialAccounts,
            additionalIncome,
            expenses,
          },
          ...(Object.keys(homeData).length > 0 && {
            home: homeData as ApplicationFormData["home"],
          }),
          ...(Object.keys(pricingData).length > 0 && {
            pricing: pricingData,
          }),
          ...(Object.keys(landData).length > 0 && {
            land: landData as ApplicationFormData["land"],
          }),
          salesRepName: result.application?.sales_rep_name ?? undefined,
          createdAt: result.application?.created_at ?? undefined,
          updatedAt: result.borrower?.updated_at ?? undefined,
        });

        // Fetch progress + milestone to populate signature statuses immediately
        // so the Summary page shows the correct completion percentage without
        // requiring the user to visit the Signatures step first.
        try {
          const progressResp = await api.calculateProgress(applicationId);

          const progress = progressResp.data as {
            application_status?: string;
            application_milestone?: string;
            borrower?: DtoBorrowerProgress;
            co_borrowers?: DtoCoBorrowerProgress[];
          };

          const progressValidationErrors = {
            ...mapBorrowerProgressToFieldErrors(
              progress.borrower,
              "primaryBorrower",
            ),
          };
          if (result.coBorrowerIds.length > 0 && progress.co_borrowers?.[0]) {
            Object.assign(
              progressValidationErrors,
              mapBorrowerProgressToFieldErrors(
                progress.co_borrowers[0],
                "coBorrower",
              ),
            );
          }
          setBackendValidationErrors(progressValidationErrors);

          const mapBorrowerStatus = (
            backendStatus: string | undefined,
          ): SignatureStatus => {
            switch (backendStatus) {
              case DomainBorrowerStatus.BorrowerReadyToSignSubmit:
                return "ready";
              case DomainBorrowerStatus.BorrowerSigned:
                return "signed";
              case DomainBorrowerStatus.BorrowerDeclinedSign:
                return "declined";
              case DomainBorrowerStatus.BorrowerWaitingOnESigSvc:
                return "validating";
              default:
                return "not-ready";
            }
          };

          // Populate signature status from server WITHOUT marking fields dirty.
          // Using updateSignature/updateBorrowerSignatureStatus would call
          // updateData, adding "signature.*" to dirtyFields. There is no
          // signature save handler, so those dirty fields would never be
          // confirmed-saved, creating an infinite no-op save loop.
          const signaturePopulate: Partial<SignatureData> = {};
          if (progress.application_status) {
            signaturePopulate.applicationStatus = progress.application_status;
          }
          if (progress.application_milestone) {
            signaturePopulate.appMilestone = progress.application_milestone;
          }
          if (progress.borrower) {
            signaturePopulate.borrowerStatus = mapBorrowerStatus(
              progress.borrower.status,
            );
          }
          if (progress.co_borrowers?.length) {
            signaturePopulate.coBorrowerStatus = mapBorrowerStatus(
              progress.co_borrowers[0].status,
            );
          }
          if (Object.keys(signaturePopulate).length > 0) {
            store.populateFromApi({ signature: signaturePopulate });
          }
        } catch (err) {
          console.warn(
            "[DGA] Failed to fetch application progress on load:",
            err,
          );
        }

        // Open existing apps on the Summary page
        store.setCurrentStep("summary");
        store.setIsLoading(false);
      } catch {
        store.setIsLoading(false);
        setPendingToast(
          "Failed to load application. Please try again.",
          "error",
        );
      }
    })();
  }, []);

  // Track scroll position to expand/collapse the next button
  const [isScrolledToBottom, setIsScrolledToBottom] = React.useState(false);

  // Ref and state for measuring content width (for navigation toolbar button expansion)
  const contentRef = React.useRef<HTMLDivElement>(null);
  const [contentWidth, setContentWidth] = React.useState<number>(628);

  // Measure content width on mount and resize
  React.useEffect(() => {
    const element = contentRef.current;
    if (!element) return;

    const updateWidth = () => {
      setContentWidth(element.offsetWidth);
    };

    updateWidth();

    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(element);

    return () => resizeObserver.disconnect();
  }, []);

  // Ref and state for the scrollable body element (for scroll-spy)
  const bodyRef = React.useRef<HTMLDivElement>(null);
  const [bodyElement, setBodyElement] = React.useState<HTMLDivElement | null>(
    null,
  );

  // Sync ref to state after DOM updates
  React.useEffect(() => {
    if (!isOpen) {
      setBodyElement(null);
      return;
    }

    const checkRef = () => {
      if (bodyRef.current && bodyRef.current !== bodyElement) {
        setBodyElement(bodyRef.current);
      }
    };

    const frameId = requestAnimationFrame(checkRef);
    return () => cancelAnimationFrame(frameId);
  }, [isOpen, bodyElement]);

  // Check if on final step (for keeping button expanded)
  const isOnFinalStep =
    STEP_ORDER.indexOf(currentStep) === STEP_ORDER.length - 1;

  const handleBodyScroll = React.useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      if (isOnFinalStep) {
        setIsScrolledToBottom(true);
        return;
      }
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      const buffer = 100;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - buffer;
      setIsScrolledToBottom(isNearBottom);
    },
    [isOnFinalStep],
  );

  // Validate all fields: flush saves, call backend progress, run Zod field validation
  const [validating, setValidating] = React.useState(false);
  const [hasTriggeredManualValidation, setHasTriggeredManualValidation] =
    React.useState(false);

  // Reset manual-validation mode when the modal closes or a different app is opened.
  React.useEffect(() => {
    if (!isOpen) {
      setHasTriggeredManualValidation(false);
      setBackendValidationErrors({});
      setManualFieldValidationActive(false);
    }
  }, [isOpen, setBackendValidationErrors, setManualFieldValidationActive]);

  React.useEffect(() => {
    setHasTriggeredManualValidation(false);
    setBackendValidationErrors({});
    setManualFieldValidationActive(false);
  }, [
    applicationId,
    setBackendValidationErrors,
    setManualFieldValidationActive,
  ]);

  const applyValidationResults = React.useCallback(
    (errors: Record<string, string>, updateFieldErrors: boolean) => {
      const { primaryPassed, coPassed } = computeValidationFlags(errors);
      setValidationErrors(errors);
      store.setFrontendValidationPrimaryPassed(primaryPassed);
      store.setFrontendValidationCoPassed(coPassed);

      if (updateFieldErrors) {
        store.setAllFieldErrors({
          ...preserveApiFieldErrors(storeRef.current.fieldErrors),
          ...errors,
        });
      }
    },
    [store, setValidationErrors],
  );

  React.useEffect(() => {
    if (store.isLoading) return;

    const errors = mergeValidationErrors(
      validateApplication(data),
      backendValidationErrors,
    );
    const { primaryPassed, coPassed } = computeValidationFlags(errors);
    setValidationErrors(errors);
    setFrontendValidationPrimaryPassed(primaryPassed);
    setFrontendValidationCoPassed(coPassed);
  }, [
    data,
    backendValidationErrors,
    store.isLoading,
    setValidationErrors,
    setFrontendValidationPrimaryPassed,
    setFrontendValidationCoPassed,
  ]);

  // Stable ref so the re-validation effect can call the latest version without
  // listing applyValidationResults as a dep (its identity changes on every store
  // state update, which would cause an infinite re-validation loop).
  const applyValidationResultsRef = React.useRef(applyValidationResults);
  applyValidationResultsRef.current = applyValidationResults;

  const flushPendingSave = React.useCallback(
    async (options?: {
      suppressHandlerToasts?: boolean;
      includePausedFields?: boolean;
    }) => {
      if (dirtyFields.size === 0 && !isSaving) return;
      await flushSave(dirtyFields, options);
    },
    [dirtyFields, isSaving, flushSave],
  );

  const flushPendingSaveRef = React.useRef(flushPendingSave);
  flushPendingSaveRef.current = flushPendingSave;

  const onBeforeSignatureProgressCheck = React.useCallback(async () => {
    await flushPendingSaveRef.current({
      suppressHandlerToasts: true,
      includePausedFields: false,
    });
  }, []);

  // "Check required fields" button — runs validation, highlights fields, shows toast.
  const handleValidateAll = React.useCallback(async () => {
    const appId = data.applicationId;
    if (!appId) return;

    setValidating(true);
    try {
      // Persist any pending edits first so the check reflects what the backend
      // actually has, not just the in-memory store.
      await flushPendingSaveRef.current({
        suppressHandlerToasts: true,
        includePausedFields: true,
      });

      const currentData = dataRef.current;

      // Backend treats nil dependents_ages_years as incomplete; empty [] means
      // "no dependents". Persist before the progress check for apps that never
      // opened the dependents UI.
      const persistDependentsAgesYears = async () => {
        const saves: Promise<unknown>[] = [];
        if (
          currentData.primaryBorrowerId &&
          currentData.primaryBorrower.maritalStatus
        ) {
          saves.push(
            api.saveBorrower(
              currentData.primaryBorrower,
              currentData.primaryBorrowerId,
              new Set(["primaryBorrower.dependentsAgesYears"]),
            ),
          );
        }
        if (
          currentData.hasCoBorrower &&
          currentData.coBorrower &&
          currentData.coBorrowerId &&
          currentData.coBorrower.maritalStatus
        ) {
          saves.push(
            api.saveBorrower(
              currentData.coBorrower,
              currentData.coBorrowerId,
              new Set(["coBorrower.dependentsAgesYears"]),
            ),
          );
        }
        if (saves.length > 0) await Promise.all(saves);
      };
      await persistDependentsAgesYears();

      try {
        const skips: Promise<unknown>[] = [];
        if (currentData.primaryBorrowerId) {
          skips.push(
            api.skipEmptyOptionalSections(
              currentData.primaryBorrower,
              currentData.primaryBorrowerId,
            ),
          );
        }
        if (
          currentData.hasCoBorrower &&
          currentData.coBorrower &&
          currentData.coBorrowerId
        ) {
          skips.push(
            api.skipEmptyOptionalSections(
              currentData.coBorrower,
              currentData.coBorrowerId,
            ),
          );
        }
        if (skips.length > 0) await Promise.all(skips);
      } catch (e) {
        // Skip-section uses the DLA /v1 route and may 401 from dealer-portal
        // auth; validation must still run and surface field highlights.
        console.error("[DGA] skip empty optional sections failed:", e);
      }

      const frontendErrors = validateApplication(currentData);
      let errors = frontendErrors;

      // Augment with the backend's own validation failures so the user sees
      // exactly which field the server still considers missing (e.g.
      // consent → date_of_birth), even when the frontend believes it is filled.
      try {
        const progressResp = await api.calculateProgress(appId);
        const progress = progressResp.data as {
          borrower?: DtoBorrowerProgress;
          co_borrowers?: DtoCoBorrowerProgress[];
        };
        const progressValidationErrors = {
          ...mapBorrowerProgressToFieldErrors(
            progress.borrower,
            "primaryBorrower",
          ),
        };
        if (currentData.hasCoBorrower && progress.co_borrowers?.[0]) {
          Object.assign(
            progressValidationErrors,
            mapBorrowerProgressToFieldErrors(
              progress.co_borrowers[0],
              "coBorrower",
            ),
          );
        }
        setBackendValidationErrors(progressValidationErrors);
        errors = mergeValidationErrors(
          frontendErrors,
          progressValidationErrors,
          {
            freshBackend: true,
          },
        );
      } catch (e) {
        console.error("[DGA] backend progress check failed:", e);
      }

      const errorEntries = Object.entries(errors);

      applyValidationResults(errors, true);
      setHasTriggeredManualValidation(true);
      setManualFieldValidationActive(true);

      // Show error toast listing top-level TOC sections with errors
      if (errorEntries.length > 0) {
        const errorMessage = `The following sections need to be completed:\n\n${summarizeErrorSections(
          errors,
        )
          .map((label) => `• ${label}`)
          .join("\n")}`;

        toast.error(errorMessage);
      }
    } catch (err) {
      console.error("[DGA] Validate all fields failed:", err);
    } finally {
      setValidating(false);
    }
  }, [
    data,
    store,
    api,
    applyValidationResults,
    setBackendValidationErrors,
    setManualFieldValidationActive,
  ]);

  // Re-validate on application data changes after the user has manually
  // triggered validation once. Updates signing flags and field highlights
  // without showing toasts.
  //
  // Intentionally depends only on `data`, not on backendValidationErrors /
  // validateAllFields. Re-running when the backend snapshot updates races
  // with handleValidateAll and can clear fieldErrors right after the toast.
  //
  // applyValidationResults is called via applyValidationResultsRef so its
  // identity is not a dep (would loop: setAllFieldErrors → store changes →
  // new callback → effect re-fires).
  React.useEffect(() => {
    if (!hasTriggeredManualValidation) return;

    const errors = mergeValidationErrors(
      validateApplication(data),
      storeRef.current.backendValidationErrors,
    );
    applyValidationResultsRef.current(errors, true);
  }, [hasTriggeredManualValidation, data]);

  // Derive which steps have validation errors based on fieldError key prefixes
  const stepsWithErrors = React.useMemo(() => {
    const steps = new Set<string>();
    for (const fieldPath of Object.keys(store.fieldErrors)) {
      if (fieldPath.startsWith("primaryBorrower."))
        steps.add("primary-borrower");
      else if (fieldPath.startsWith("coBorrower.")) steps.add("co-borrower");
      else if (fieldPath.startsWith("home.") || fieldPath.startsWith("land."))
        steps.add("property");
      else if (fieldPath.startsWith("pricing.")) steps.add("pricing");
      else if (fieldPath.startsWith("lenders.")) steps.add("lenders");
    }
    return steps;
  }, [store.fieldErrors]);

  // Derive which sub-sections have errors for TOC sub-item highlighting.
  // Maps error key patterns to sub-section IDs.
  const subSectionsWithErrors = React.useMemo(() => {
    const subs = new Set<string>();
    for (const key of Object.keys(store.fieldErrors)) {
      // Primary borrower sub-sections
      if (key.startsWith("primaryBorrower.")) {
        const field = key.slice("primaryBorrower.".length);
        if (
          field.startsWith("firstName") ||
          field.startsWith("lastName") ||
          field.startsWith("middleName") ||
          field.startsWith("suffix") ||
          field.startsWith("email") ||
          field.startsWith("phone") ||
          field.startsWith("citizenship")
        ) {
          subs.add("personal-info");
        } else if (
          field.startsWith("dateOfBirth") ||
          field.startsWith("ssn") ||
          field.startsWith("bankruptcyDate")
        ) {
          subs.add("pii");
        } else if (
          field.startsWith("maritalStatus") ||
          field.startsWith("dependentsAgesYears")
        ) {
          subs.add("marital-status");
        } else if (
          field.startsWith("biologicalSex") ||
          field.startsWith("ethnicities") ||
          field.startsWith("races")
        ) {
          subs.add("ecoa");
        } else if (field.startsWith("addresses")) {
          subs.add("address");
        } else if (
          field.startsWith("nearestRelativeName") ||
          field.startsWith("nearestRelativeRelationship") ||
          field.startsWith("nearestRelativePhone")
        ) {
          subs.add("nearest-relative");
        } else if (
          field.startsWith("employmentHistory") ||
          field.startsWith("employmentStatus")
        ) {
          subs.add("employment");
        } else if (field.startsWith("financialAccounts")) {
          subs.add("financial");
        } else if (field.startsWith("additionalIncome")) {
          subs.add("income");
        } else if (field.startsWith("expenses")) {
          subs.add("expenses");
        }
      }

      // Co-borrower sub-sections (prefixed with "co-")
      if (key.startsWith("coBorrower.")) {
        const field = key.slice("coBorrower.".length);
        if (
          field.startsWith("firstName") ||
          field.startsWith("lastName") ||
          field.startsWith("middleName") ||
          field.startsWith("suffix") ||
          field.startsWith("email") ||
          field.startsWith("phone") ||
          field.startsWith("citizenship")
        ) {
          subs.add("co-personal-info");
        } else if (
          field.startsWith("dateOfBirth") ||
          field.startsWith("ssn") ||
          field.startsWith("bankruptcyDate")
        ) {
          subs.add("co-pii");
        } else if (
          field.startsWith("maritalStatus") ||
          field.startsWith("dependentsAgesYears")
        ) {
          subs.add("co-marital-status");
        } else if (
          field.startsWith("biologicalSex") ||
          field.startsWith("ethnicities") ||
          field.startsWith("races")
        ) {
          subs.add("co-ecoa");
        } else if (field.startsWith("addresses")) {
          subs.add("co-address");
        } else if (
          field.startsWith("nearestRelativeName") ||
          field.startsWith("nearestRelativeRelationship") ||
          field.startsWith("nearestRelativePhone")
        ) {
          subs.add("co-nearest-relative");
        } else if (
          field.startsWith("employmentHistory") ||
          field.startsWith("employmentStatus")
        ) {
          subs.add("co-employment");
        } else if (field.startsWith("financialAccounts")) {
          subs.add("co-financial");
        } else if (field.startsWith("additionalIncome")) {
          subs.add("co-income");
        } else if (field.startsWith("expenses")) {
          subs.add("co-expenses");
        }
      }

      // Property sub-sections
      if (key.startsWith("home.")) {
        subs.add("home-details");
      }
      if (key.startsWith("land.")) {
        if (key.startsWith("land.propertyAddress")) {
          subs.add("property-address");
        } else {
          subs.add("land-details");
        }
      }
    }
    return subs;
  }, [store.fieldErrors]);

  // TOC icon with optional error dot overlay
  const tocIcon = (stepId: string, active: boolean) => (
    <div className="relative">
      {STEP_CONFIG[stepId as ApplicationStep].icon(active)}
      {stepsWithErrors.has(stepId) && (
        <div className="absolute -top-0.5 -right-0.5 size-2 rounded-full bg-[#df1b41] ring-1 ring-white" />
      )}
    </div>
  );

  // Build TOC items with icons and subsections
  const tocItems: TableOfContentsItem[] = [
    {
      id: "summary",
      label: STEP_CONFIG["summary"].label,
      icon: (active: boolean) => tocIcon("summary", active),
      disabled: false,
    },
    {
      id: "primary-borrower",
      label: STEP_CONFIG["primary-borrower"].label,
      icon: (active: boolean) => tocIcon("primary-borrower", active),
      completed:
        getStepProgress("primary-borrower") === 100 &&
        !stepsWithErrors.has("primary-borrower"),
      disabled: isLockedForSigning,
      children: [
        {
          id: "personal-info",
          label: "Personal Information",
          hasError: subSectionsWithErrors.has("personal-info"),
        },
        { id: "pii", label: "PII", hasError: subSectionsWithErrors.has("pii") },
        {
          id: "marital-status",
          label: "Marital Status",
          hasError: subSectionsWithErrors.has("marital-status"),
        },
        {
          id: "ecoa",
          label: "ECOA",
          hasError: subSectionsWithErrors.has("ecoa"),
        },
        {
          id: "address",
          label: "Address",
          hasError: subSectionsWithErrors.has("address"),
        },
        {
          id: "nearest-relative",
          label: "Nearest Relative",
          hasError: subSectionsWithErrors.has("nearest-relative"),
        },
        {
          id: "employment",
          label: "Employment History",
          hasError: subSectionsWithErrors.has("employment"),
        },
        {
          id: "financial",
          label: "Financial Accounts",
          hasError: subSectionsWithErrors.has("financial"),
        },
        {
          id: "income",
          label: "Additional Income",
          hasError: subSectionsWithErrors.has("income"),
        },
        {
          id: "expenses",
          label: "Expenses",
          hasError: subSectionsWithErrors.has("expenses"),
        },
      ],
    },
    {
      id: "co-borrower",
      label: STEP_CONFIG["co-borrower"].label,
      icon: (active: boolean) => tocIcon("co-borrower", active),
      completed:
        data.hasCoBorrower &&
        getStepProgress("co-borrower") === 100 &&
        !stepsWithErrors.has("co-borrower"),
      disabled: isLockedForSigning,
      children: data.hasCoBorrower
        ? [
            {
              id: "co-personal-info",
              label: "Personal Information",
              hasError: subSectionsWithErrors.has("co-personal-info"),
            },
            {
              id: "co-pii",
              label: "PII",
              hasError: subSectionsWithErrors.has("co-pii"),
            },
            {
              id: "co-marital-status",
              label: "Marital Status",
              hasError: subSectionsWithErrors.has("co-marital-status"),
            },
            {
              id: "co-ecoa",
              label: "ECOA",
              hasError: subSectionsWithErrors.has("co-ecoa"),
            },
            {
              id: "co-address",
              label: "Address",
              hasError: subSectionsWithErrors.has("co-address"),
            },
            {
              id: "co-nearest-relative",
              label: "Nearest Relative",
              hasError: subSectionsWithErrors.has("co-nearest-relative"),
            },
            {
              id: "co-employment",
              label: "Employment History",
              hasError: subSectionsWithErrors.has("co-employment"),
            },
            {
              id: "co-financial",
              label: "Financial Accounts",
              hasError: subSectionsWithErrors.has("co-financial"),
            },
            {
              id: "co-income",
              label: "Additional Income",
              hasError: subSectionsWithErrors.has("co-income"),
            },
            {
              id: "co-expenses",
              label: "Expenses",
              hasError: subSectionsWithErrors.has("co-expenses"),
            },
            {
              id: "co-remove",
              label: "Remove Co-Borrower",
            },
          ]
        : [{ id: "add-co-borrower", label: "Add Co-Borrower" }],
    },
    {
      id: "property",
      label: STEP_CONFIG["property"].label,
      icon: (active: boolean) => tocIcon("property", active),
      completed:
        getStepProgress("property") === 100 && !stepsWithErrors.has("property"),
      disabled: isLockedForSigning,
      children: [
        {
          id: "home-details",
          label: "Home Details",
          hasError: subSectionsWithErrors.has("home-details"),
        },
        {
          id: "property-address",
          label: "Property Address",
          hasError: subSectionsWithErrors.has("property-address"),
        },
        {
          id: "land-details",
          label: "Land Details",
          hasError: subSectionsWithErrors.has("land-details"),
        },
      ],
    },
    {
      id: "pricing",
      label: STEP_CONFIG["pricing"].label,
      icon: (active: boolean) => tocIcon("pricing", active),
      completed:
        getStepProgress("pricing") === 100 && !stepsWithErrors.has("pricing"),
      disabled: isLockedForSigning,
    },
    {
      id: "lenders",
      label: STEP_CONFIG["lenders"].label,
      icon: (active: boolean) => tocIcon("lenders", active),
      disabled: isLockedForSigning,
    },
    {
      id: "signature",
      label: STEP_CONFIG["signature"].label,
      icon: (active: boolean) => tocIcon("signature", active),
      completed:
        getStepProgress("signature") === 100 &&
        !stepsWithErrors.has("signature"),
      disabled: false,
    },
  ];

  // Get section IDs for current step (for scroll-spy)
  const currentStepSectionIds = React.useMemo(() => {
    const item = tocItems.find((item) => item.id === currentStep);
    return item?.children?.map((child) => child.id) ?? [];
  }, [currentStep, data.hasCoBorrower]);

  // Track which section is visible for TOC highlighting
  const activeSubId = useScrollSpy({
    sectionIds: currentStepSectionIds,
    container: bodyElement,
  });

  // Handle TOC sub-item click - scroll to section
  const handleSubItemClick = React.useCallback(
    (parentId: string, subId: string) => {
      setCurrentStep(parentId as ApplicationStep);
      requestAnimationFrame(() => {
        const element = document.getElementById(subId);
        const container = bodyRef.current;
        if (element && container) {
          const elementTop = element.offsetTop;
          container.scrollTo({
            top: elementTop,
            behavior: "smooth",
          });
        }
      });
    },
    [setCurrentStep],
  );

  // Handle TOC item click - redirect to signature when locked
  const handleItemClick = React.useCallback(
    (id: string) => {
      if (isLockedForSigning && id !== "summary" && id !== "signature") {
        setCurrentStep("signature");
        return;
      }
      setCurrentStep(id as ApplicationStep);
    },
    [isLockedForSigning, setCurrentStep],
  );

  // Reset scroll position and button state when section changes
  React.useEffect(() => {
    requestAnimationFrame(() => {
      const container = bodyRef.current;
      if (container) {
        container.scrollTo({ top: 0, behavior: "instant" });
        if (isOnFinalStep) {
          setIsScrolledToBottom(true);
          return;
        }
        const hasScrollbar = container.scrollHeight > container.clientHeight;
        setIsScrolledToBottom(!hasScrollbar);
      }
    });
  }, [currentStep, isOnFinalStep]);

  // Build navigation steps
  const navSteps: NavigationStep[] = STEP_ORDER.map((step) => ({
    id: step,
    label: STEP_CONFIG[step].label,
    completed: getStepProgress(step) === 100 && !stepsWithErrors.has(step),
    progress: getStepProgress(step),
  }));

  const currentStepIndex = STEP_ORDER.indexOf(currentStep);
  const overallProgress = getOverallProgress();

  const renderStepContent = (step: ApplicationStep = currentStep) => {
    switch (step) {
      case "summary":
        return (
          <SummarySection
            onNavigate={(s: ApplicationStep) => setCurrentStep(s)}
            isLockedForSigning={isLockedForSigning}
          />
        );
      case "primary-borrower":
        return (
          <div className="space-y-6">
            <div id="personal-info">
              <PersonalInformationForm />
            </div>
            <div id="pii">
              <PIIForm />
            </div>
            <div id="marital-status">
              <MaritalStatusForm />
            </div>
            <div id="ecoa">
              <EcoaForm />
            </div>
            <div id="address">
              <AddressFormSection />
            </div>
            <div id="nearest-relative">
              <NearestRelativeForm />
            </div>
            <div id="employment">
              <EmploymentHistoryForm />
            </div>
            <div id="financial">
              <FinancialAccountsForm />
            </div>
            <div id="income">
              <AdditionalIncomeForm />
            </div>
            <div id="expenses">
              <ExpensesForm />
            </div>
          </div>
        );
      case "co-borrower":
        return (
          <div className="space-y-6">
            {data.hasCoBorrower ? (
              <>
                <div id="co-personal-info">
                  <PersonalInformationForm isCoBorrower />
                </div>
                <div id="co-pii">
                  <PIIForm isCoBorrower />
                </div>
                <div id="co-marital-status">
                  <MaritalStatusForm isCoBorrower />
                </div>
                <div id="co-ecoa">
                  <EcoaForm isCoBorrower />
                </div>
                <div id="co-address">
                  <AddressFormSection isCoBorrower />
                </div>
                <div id="co-nearest-relative">
                  <NearestRelativeForm isCoBorrower />
                </div>
                <div id="co-employment">
                  <EmploymentHistoryForm isCoBorrower />
                </div>
                <div id="co-financial">
                  <FinancialAccountsForm isCoBorrower />
                </div>
                <div id="co-income">
                  <AdditionalIncomeForm isCoBorrower />
                </div>
                <div id="co-expenses">
                  <ExpensesForm isCoBorrower />
                </div>
                <div id="co-remove">
                  <RemoveCoBorrowerSection
                    isLoading={removeCoBorrowerApi.isLoading}
                    isAppLoading={store.isLoading}
                    onConfirm={() => void removeCoBorrowerApi.execute()}
                  />
                </div>
              </>
            ) : (
              <div
                id="add-co-borrower"
                className="flex flex-col justify-center items-center py-16"
              >
                <div className="flex justify-center items-center mb-8 h-[100px] rounded-full bg-grey-100">
                  <CoBorrowerEmptyStateGraphic />
                </div>
                <h3 className="mb-3 text-[18px] font-semibold text-on-surface-primary">
                  No co-borrower added yet
                </h3>
                <p className="mb-8 max-w-sm text-base text-center text-on-surface-muted">
                  Co-borrowers will appear here once added.
                </p>
                <AddCoBorrowerDropdown
                  onAddHere={handleAddCoBorrowerHere}
                  onInviteViaEmail={() => setInviteDialogOpen(true)}
                  disabled={isCreatingCoBorrower}
                />
              </div>
            )}
          </div>
        );
      case "property":
        return (
          <div className="space-y-6">
            <div id="home-details">
              <HomeDetailsForm />
            </div>
            <div id="property-address">
              <PropertyAddressForm />
            </div>
            <div id="land-details">
              <LandDetailsForm />
            </div>
          </div>
        );
      case "pricing":
        return <PricingCalculatorSection />;
      case "lenders":
        return <LenderSelectionSection />;
      case "signature":
        return (
          <SignatureSection
            onCheckRequiredFields={() => void handleValidateAll()}
            validating={validating}
            onBeforeProgressCheck={onBeforeSignatureProgressCheck}
          />
        );
      default:
        return null;
    }
  };

  // Show first step for new applications (no applicationId from props or store)
  const isNewDraft = !data.applicationId && !applicationId;

  if (isNewDraft) {
    return (
      <FullscreenModalContent
        isCloseLoading={isCloseLoading}
        headerContent={
          <div className="flex gap-2 items-center">
            <FileAddIcon
              variant="bulk-rounded"
              size={14}
              className="text-primary"
            />
            <FullscreenModalTitle className="font-semibold text-md text-on-surface-primary">
              Create Application
            </FullscreenModalTitle>
          </div>
        }
        showSidebar={false}
      >
        <DraftCreationStep
          onDraftCreated={(appId, borrowerId, formData) => {
            // Seed store with borrower data FIRST (no dirty marks)
            store.populateFromApi({
              primaryBorrower: {
                ...store.data.primaryBorrower,
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email,
                phone: formData.phone,
              },
            });
            // Then set IDs — this triggers deferred field flush (lenders POST)
            store.updateApplicationIds(appId, borrowerId);
          }}
        />
      </FullscreenModalContent>
    );
  }

  return (
    <FullscreenModalContent
      bodyRef={bodyRef}
      isCloseLoading={isCloseLoading}
      headerProgress={
        !store.isLoading && currentStep !== "summary"
          ? overallProgress
          : undefined
      }
      sidebarSeparated={false}
      onBodyScroll={handleBodyScroll}
      headerContent={
        <div className="flex gap-2 items-center">
          <FileAddIcon
            variant="bulk-rounded"
            size={14}
            className="text-primary"
          />
          <FullscreenModalTitle className="font-semibold text-md text-on-surface-primary">
            Create Application
          </FullscreenModalTitle>
          {!store.isLoading &&
            data.applicationId &&
            data.primaryBorrower.firstName &&
            data.primaryBorrower.lastName && (
              <>
                <div className="flex gap-2 items-center">
                  <ChevronRightIcon
                    variant="stroke-sharp"
                    size={7}
                    className="text-on-surface-muted"
                  />
                  <div className="flex gap-1 items-center p-2 h-6 rounded-lg">
                    <div className="flex items-center justify-center w-3 h-3 rounded-full bg-[#e5ebfd] text-primary text-[5.25px] font-semibold uppercase">
                      {data.primaryBorrower.lastName.charAt(0).toUpperCase()}
                    </div>
                    <span className="text-xs font-medium text-on-surface-primary">
                      {data.primaryBorrower.firstName}{" "}
                      {data.primaryBorrower.lastName} — {data.applicationId}
                    </span>
                  </div>
                </div>
                <div className="flex gap-1 items-center px-2 py-[6px] bg-[#EDEDF0] rounded-full">
                  <PencilEdit02Icon
                    variant="stroke-rounded"
                    size={9}
                    className="text-[#111827]"
                  />
                  <span className="text-xs font-medium text-[#111827] leading-none">
                    Draft
                  </span>
                </div>
              </>
            )}
        </div>
      }
      headerRight={
        !store.isLoading && currentStep !== "summary" ? (
          <DgaSaveStatus
            isSaving={isSaving}
            lastError={lastError}
            lastSavedAt={lastSavedAt}
            hasUnsavedChanges={hasUnsavedChanges}
          />
        ) : undefined
      }
      showSidebar={!store.isLoading && currentStep !== "summary"}
      sidebarWidth={"max(calc(50% - 326px), 326px)"}
      sidebarContent={
        <div className="flex justify-start pt-10 pl-10 pb-34">
          <TableOfContents
            items={tocItems}
            activeId={currentStep}
            activeSubId={activeSubId ?? currentStepSectionIds[0]}
            onItemClick={handleItemClick}
            onSubItemClick={handleSubItemClick}
          />
        </div>
      }
    >
      {/* Main Content */}
      <div
        ref={contentRef}
        className={
          store.isLoading
            ? "w-full flex justify-center"
            : currentStep === "summary"
              ? "w-full px-10 py-8"
              : "w-[628px] py-8 pb-32"
        }
      >
        {store.isLoading ? (
          <div className="flex justify-center items-center pt-32 h-full">
            <Spinner variant="blue" size="m" />
          </div>
        ) : (
          <Transition
            variant="slide"
            transitionKey={currentStep}
            getIndex={(step) => STEP_ORDER.indexOf(step)}
          >
            {(step) => renderStepContent(step)}
          </Transition>
        )}
      </div>

      {/* Bottom Navigation (hidden on Summary) */}
      {!store.isLoading && currentStep !== "summary" && (
        <NavigationToolbar
          steps={navSteps}
          currentStepIndex={currentStepIndex}
          onNavigate={(index) => setCurrentStep(STEP_ORDER[index])}
          onPrimaryAction={
            currentStepIndex === STEP_ORDER.length - 1
              ? () => void handleSubmitApplication()
              : undefined
          }
          primaryActionLabel={
            currentStepIndex === STEP_ORDER.length - 1
              ? "Submit Application"
              : "Continue"
          }
          primaryActionDisabled={
            currentStepIndex === STEP_ORDER.length - 1
              ? !readyForLenders
              : undefined
          }
          overallProgress={overallProgress}
          floating
          isScrolledToBottom={isScrolledToBottom}
          contentWidth={contentWidth}
          moreMenuContent={
            <DropdownMenuItem
              onClick={() => {
                void handleValidateAll();
              }}
              disabled={validating || !data.applicationId || isSaving}
              className="gap-3"
            >
              <AlertCircleIcon size={16} />
              {validating ? "Validating..." : "Check required fields"}
            </DropdownMenuItem>
          }
        />
      )}
      <InviteCoBorrowerDialog
        open={inviteDialogOpen}
        onOpenChange={setInviteDialogOpen}
        dealerId={dealerId ?? ""}
        applicationId={data.applicationId ?? ""}
      />

      <ApplicationLockConflictDialog
        open={showConflictModal}
        onOpenChange={(open) => {
          if (!open) dismissConflict();
        }}
        onTakeover={handleTakeover}
        lockedByUser={conflictLockedByUser}
      />
    </FullscreenModalContent>
  );
};

// ============================================================================
// Application Modal (controlled component)
// ============================================================================

interface ApplicationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  applicationId?: string;
}

const ApplicationModal: React.FC<ApplicationModalProps> = ({
  open,
  onOpenChange,
  applicationId,
}) => {
  const { unlockApplication } = useDgaApi();
  const { registerCloseHandler } = useDgaModal();
  const { dealer_id: dealerId } = useDealer();
  const lockedAppIdRef = React.useRef<string | null>(null);
  const dealerIdRef = React.useRef<string | undefined>(dealerId);
  const unlockApplicationRef = React.useRef(unlockApplication);
  unlockApplicationRef.current = unlockApplication;
  const [isClosing, setIsClosing] = React.useState(false);
  const isClosingRef = React.useRef(false);
  isClosingRef.current = isClosing;

  // Keep dealerId ref updated (for beforeunload handler)
  React.useEffect(() => {
    dealerIdRef.current = dealerId;
  }, [dealerId]);

  // Track the locked application ID when modal opens with an existing app.
  // (Lock is acquired by ApplicationTable before openDgaModal is called.)
  React.useEffect(() => {
    if (open && applicationId) {
      lockedAppIdRef.current = applicationId;
    }
  }, [open, applicationId]);

  React.useEffect(() => {
    if (open) {
      setIsClosing(false);
    }
  }, [open]);

  const closeWithUnlock = React.useCallback(async () => {
    if (isClosingRef.current) return;

    if (lockedAppIdRef.current) {
      const appIdToUnlock = lockedAppIdRef.current;
      lockedAppIdRef.current = null;
      setIsClosing(true);
      try {
        await unlockApplicationRef.current(appIdToUnlock);
      } catch (error) {
        console.error(error);
      } finally {
        setIsClosing(false);
      }
    }

    onOpenChange(false);
  }, [onOpenChange]);

  React.useEffect(() => {
    if (!open) return;
    registerCloseHandler(closeWithUnlock);
    return () => registerCloseHandler(null);
  }, [open, closeWithUnlock, registerCloseHandler]);

  // Intercept Radix dismiss (X / Escape) so unlock completes before the modal
  // closes and the application list refresh runs.
  const handleCloseWithUnlock = React.useCallback(
    (nextOpen: boolean) => {
      if (nextOpen) {
        onOpenChange(true);
        return;
      }
      if (isClosingRef.current) return;
      void closeWithUnlock();
    },
    [closeWithUnlock, onOpenChange],
  );

  const jwtTokenRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    const updateToken = async () => {
      const user = auth.currentUser;
      if (user) {
        const token = await user.getIdToken();
        jwtTokenRef.current = token;
      }
    };

    void updateToken();

    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        void user.getIdToken().then((token) => {
          jwtTokenRef.current = token;
        });
      } else {
        jwtTokenRef.current = null;
      }
    });

    return () => unsubscribe();
  }, []);

  // Unlock on tab/window close
  React.useEffect(() => {
    const handleBeforeUnload = () => {
      if (!open || !jwtTokenRef.current) return;

      const appIdToUnlock = lockedAppIdRef.current;
      if (!appIdToUnlock || !dealerIdRef.current) return;

      const url = `${__API_BASE_URL__}${API_PREFIX_PATH}/${dealerIdRef.current}/application/${appIdToUnlock}/unlock`;

      void fetch(url, {
        method: "POST",
        keepalive: true,
        headers: {
          "x-jwt": jwtTokenRef.current,
          "Content-Type": "application/json",
        },
      });
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [open]);

  React.useEffect(() => {
    if (!open || __ENV__ !== "prod") return;
    const replay = Sentry.getReplay();
    if (!replay) return;
    // stop() in buffer mode discards the pre-modal buffer without sending it;
    // start() then opens a fresh session replay scoped to the modal lifetime.
    void (async () => {
      try {
        await replay.stop();
      } catch {
        // ignore
      }
      replay.start();
    })();
    return () => {
      void (async () => {
        try {
          await replay.stop();
        } catch {
          // ignore
        }
        replay.startBuffering();
      })();
    };
  }, [open]);

  return (
    <ApplicationFormProvider
      key={applicationId ?? "new"}
      initialApplicationId={applicationId}
    >
      <FullscreenModal open={open} onOpenChange={handleCloseWithUnlock}>
        <ApplicationModalContent
          isOpen={open}
          applicationId={applicationId}
          lockedAppIdRef={lockedAppIdRef}
          isCloseLoading={isClosing}
        />
      </FullscreenModal>
    </ApplicationFormProvider>
  );
};

export default ApplicationModal;
